import { FACTORY_INSTRUMENT_PRESETS } from '../instrument/factoryPresets';
import { isPreviewPlayer } from '../chat/isPreviewPlayer';
import { instrumentLookaheadSeconds } from '../audio/playbackProfile';
import { stockInstrumentPreset, stockSampleInstrumentPatch } from '../instrument/stockSamplePresets';
import { flattenStockSections, buildSamplePool } from '../instrument/samplePool';
import { CORE_CUSTOM_TABLE } from '../instrument/synthCoreSchema';
import { encodeWavetableWav, importWavetableFile, wavetableKey } from '../instrument/wavetableImport';
import { registerCoreTable } from '../instrument/coreTableStore';
import { API_URL } from '../config/api';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useAudio } from './AudioContext';
import NativeInstrumentEngine from '../instrument/NativeInstrumentEngine';
import { transportClock } from '../audio/transportClock';
import { MIDI_TARGET_DRUM, MIDI_TARGET_INSTRUMENT } from '../audio/midiTarget';
import { eventPitches } from '../utils/noteEvents';

import {
  LIVE_EDIT_GUARD_SECONDS,
  nextAuditionState
} from '../audio/auditionInvalidation';
import {
  cloneInstrumentPatch,
  createDefaultInstrumentPatch,
  normalizeInstrumentPatch
} from '../instrument/patchSchema';
import {
  INSTRUMENT_AI_CAPABILITIES,
  applyAiPatchCommands,
  auditInstrumentPatch,
  createAiSoundSpec
} from '../instrument/aiSoundSpec';
import {
  buildSampleZones,
  filenameHasExplicitOctave,
  rootNoteFromAudioBuffer,
  rootNoteFromFilename
} from '../instrument/sampleMapping';
import { patternLengthForRecordingTake, recordedNoteTiming, appendRecordedNotes, recordingTargetClip } from '../instrument/recordingTake';
import { ArpScheduler } from '../instrument/arpeggiator';
import { releaseArpeggiatorVoices, releaseHeldInputVoices } from '../instrument/liveInputVoices';
import { resolveInstrumentSampleUrl } from '../instrument/sampleAssetUrl';
import { instrumentNotesByTrack } from '../instrument/hostedNotes';
import { availableHostedPresets } from '../instrument/hostedInstrumentPresets';
import { fetchAvailableHostedLibraries } from '../instrument/hostedSampleStore';
import { subscribeMidiInputs } from '../audio/midiInputListeners';
import { clearSchedulerInterval, setSchedulerInterval } from '../audio/schedulerClock';
import { MPE_DEFAULT_BEND_RANGE, MpeInput } from '../instrument/mpeInput';

export { FACTORY_INSTRUMENT_PRESETS };

// How long captured notes may wait before being committed. Short enough that
// the piano roll still fills in as you play, long enough that a fast run costs
// one render instead of a dozen.
const RECORDING_FLUSH_MS = 120;

// Lookahead for the note scheduler. Must exceed the 25ms tick with room for a
// stalled frame, or a late tick queues notes that are already in the past.
const LOOKAHEAD_SECONDS = instrumentLookaheadSeconds();
// The arp is played live, so it keeps the short studio window on the same worker clock.
const ARP_LOOKAHEAD_SECONDS = 0.12;
// Starting a source exactly at currentTime can still land in the render quantum
// Web Audio has already begun. Three milliseconds clears one 128-frame quantum
// at 48kHz without adding a perceptible transport offset.
const MIN_SCHEDULE_LEAD_SECONDS = 0.003;
// Scheduled-key memory is bounded: keys carry the unwrapped beat, so a long
// loop session would otherwise accumulate one per note per pass forever.
const MAX_SCHEDULED_KEYS = 4000;

const InstrumentContext = createContext(null);
const STORAGE_KEY = 'cookup:native-instrument:presets:v1';
const LAST_PATCH_KEY = 'cookup:native-instrument:last-patch:v1';
const MAX_HISTORY = 80;


const loadInitialPatch = () => {
  try {
    const stored = localStorage.getItem(LAST_PATCH_KEY);
    if (stored) return normalizeInstrumentPatch(JSON.parse(stored));
  } catch (_) { /* fall back to factory patch */ }
  return createDefaultInstrumentPatch();
};

const updateAtPath = (object, path, value) => {
  const keys = Array.isArray(path) ? path : String(path).split('.');
  const clone = JSON.parse(JSON.stringify(object));
  let cursor = clone;
  keys.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
  return clone;
};

export const useInstrument = () => {
  const value = useContext(InstrumentContext);
  if (!value) throw new Error('useInstrument must be used within InstrumentProvider');
  return value;
};

/** Optional access for shared chrome that can also render in isolated tools/tests. */
export const useOptionalInstrument = () => useContext(InstrumentContext);

export const InstrumentProvider = ({ children }) => {
  const {
    audioContext, bpm, tracks, timelineClips, isPlaying, isRecording,
    addInstrumentTrack, getTrackInput, updateTrack, addClipToTimeline, updatePatternClip,
    registerInstrumentSampleBuffers, recordingWriteClipsRef, beatAtPerformanceTime,
    getTransportSchedule
  } = useAudio();
  const [patch, setPatchState] = useState(loadInitialPatch);
  const [userPresets, setUserPresets] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored).map(normalizeInstrumentPatch) : [];
    } catch (_) {
      return [];
    }
  });
  const [stockInstrumentAssets, setStockInstrumentAssets] = useState([]);
  const [hostedLibraryIds, setHostedLibraryIds] = useState([]);
  const [presetLibraryError, setPresetLibraryError] = useState('');
  const [presetLibraryLoading, setPresetLibraryLoading] = useState(false);
  const presetLibraryRequestRef = useRef(null);
  const reloadPresetLibrary = useCallback(async () => {
    presetLibraryRequestRef.current?.abort();
    const controller = new AbortController();
    presetLibraryRequestRef.current = controller;
    let timedOut = false;
    const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 20000);
    setPresetLibraryLoading(true); setPresetLibraryError('');
    const token = sessionStorage.getItem('token');
    const currentRequest = () => presetLibraryRequestRef.current === controller && !controller.signal.aborted;
    try {
      // These catalogs are independent: stock access failing must not hide the
      // public multisampled instruments, and Refresh sounds must retry both.
      const results = await Promise.allSettled([
        fetchAvailableHostedLibraries({ signal: controller.signal, throwOnError: true }).then((ids) => {
          if (currentRequest()) setHostedLibraryIds(ids);
        }),
        isPreviewPlayer() ? Promise.resolve() : (async () => {
          const response = await fetch(`${API_URL}/api/library/stock`, {
            signal: controller.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (!response.ok) throw new Error('Instrument sample presets could not be loaded.');
          const data = await response.json();
          if (!Array.isArray(data?.sections)) throw new Error('Instrument sample catalog is invalid.');
          if (currentRequest()) setStockInstrumentAssets(flattenStockSections(data, 'instrument'));
        })()
      ]);
      if (presetLibraryRequestRef.current === controller) {
        const errors = results.filter((result) => result.status === 'rejected' && result.reason?.name !== 'AbortError');
        setPresetLibraryError(timedOut ? 'Instrument catalog loading timed out.' : errors.map((result) => result.reason.message).join(' '));
      }
    } finally {
      window.clearTimeout(timeout);
      if (presetLibraryRequestRef.current === controller) { presetLibraryRequestRef.current = null; setPresetLibraryLoading(false); }
    }
  }, []);
  useEffect(() => {
    reloadPresetLibrary();
    return () => { presetLibraryRequestRef.current?.abort(); presetLibraryRequestRef.current = null; };
  }, [reloadPresetLibrary]);
  const factoryPresets = useMemo(() => [
    ...FACTORY_INSTRUMENT_PRESETS,
    ...availableHostedPresets(hostedLibraryIds),
    ...stockInstrumentAssets.map(stockInstrumentPreset).filter(Boolean)
  ], [stockInstrumentAssets, hostedLibraryIds]);
  const stockSamplePool = useMemo(() => buildSamplePool(stockInstrumentAssets), [stockInstrumentAssets]);
  const [activeNotes, setActiveNotes] = useState([]);
  const [midiStatus, setMidiStatus] = useState(() => (
    typeof navigator !== 'undefined' && navigator.requestMIDIAccess ? 'available' : 'unavailable'
  ));
  const [midiInputs, setMidiInputs] = useState([]);
  const [selectedMidiInputId, setSelectedMidiInputIdState] = useState(() => {
    try { return localStorage.getItem('cookup_midi_input') || 'all'; } catch (_) { return 'all'; }
  });
  // MPE: 'auto' follows the controller's configuration message.
  const [mpeSettings, setMpeSettingsState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cookup_mpe') || '{}');
      return { mode: [true, false].includes(saved.mode) ? saved.mode : 'auto', bendRange: Number(saved.bendRange) || MPE_DEFAULT_BEND_RANGE };
    } catch (_) {
      return { mode: 'auto', bendRange: MPE_DEFAULT_BEND_RANGE };
    }
  });
  const [mpeActive, setMpeActive] = useState(false);
  const mpeInputRef = useRef(null);
  if (!mpeInputRef.current) mpeInputRef.current = new MpeInput({ enabled: mpeSettings.mode, bendRange: mpeSettings.bendRange });
  const [engineReady, setEngineReady] = useState(false);
  const [engineError, setEngineError] = useState(null);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const enginesRef = useRef(new Map());
  const sampleBuffersRef = useRef(new Map());
  const sampleAssetLoadsRef = useRef(new Map());
  const patchRef = useRef(patch);
  const activeTrackIdRef = useRef(activeTrackId);
  const tracksRef = useRef(tracks);
  const timelineClipsRef = useRef(timelineClips);
  const heldVoicesRef = useRef(new Map());
  const recordingNotesRef = useRef(new Map());
  const recordingTakeRef = useRef(null);
  const recordingTakeTimerRef = useRef(null);
  const isRecordingRef = useRef(false);
  const isPlayingRef = useRef(false);
  // writeRecordedNote is declared below tickArp, so the arp reaches it via a ref
  // rather than a dependency (which would be a temporal dead zone at render).
  const writeRecordedNoteRef = useRef(null);
  // What live input plays, derived from the timeline selection. DrumProvider
  // renders inside this one, so its trigger is registered upward rather than
  // consumed directly.
  const midiTargetRef = useRef(null);
  const drumTriggerRef = useRef(null);
  // Captured notes waiting to be committed, keyed by clip id. Flushed on a short
  // timer so a dense performance costs a couple of renders rather than one per
  // note; a render storm delays the lookahead scheduler and is audible.
  const pendingRecordedNotesRef = useRef(new Map());
  const recordingFlushTimerRef = useRef(null);
  const scheduledEventsRef = useRef(new Set());
  const schedulerLastBeatRef = useRef(null);
  const schedulerLastTimeRef = useRef(0);
  const schedulerTickRef = useRef(null);
  const lastAuditionClipsRef = useRef(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  // Arp input: notes it plays (latched ones stay after release), keys still
  // physically down, and press order for 'as played'.
  const heldArpNotesRef = useRef(new Map());
  const pressedArpKeysRef = useRef(new Set());
  const arpPressOrderRef = useRef(0);
  const activeArpVoicesRef = useRef(new Map());
  const arpTimerRef = useRef(null);
  const arpSchedulerRef = useRef(null);
  if (!arpSchedulerRef.current) arpSchedulerRef.current = new ArpScheduler();
  // Recently scheduled arp steps (audio time → pattern step), for the playhead.
  const arpPlayheadRef = useRef([]);
  const bpmRef = useRef(bpm);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  const midiAccessRef = useRef(null);
  const midiInputCleanupRef = useRef(null);
  const selectedMidiInputIdRef = useRef(selectedMidiInputId);

  const instrumentTracks = useMemo(() => tracks.filter((track) => track.type === 'instrument'), [tracks]);
  const activeTrack = instrumentTracks.find((track) => track.id === activeTrackId) || null;

  useEffect(() => { patchRef.current = patch; }, [patch]);
  useEffect(() => { activeTrackIdRef.current = activeTrackId; }, [activeTrackId]);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { timelineClipsRef.current = timelineClips; }, [timelineClips]);
  useEffect(() => { selectedMidiInputIdRef.current = selectedMidiInputId; }, [selectedMidiInputId]);

  const selectTrack = useCallback((trackId) => {
    const track = tracksRef.current.find((entry) => entry.id === trackId && entry.type === 'instrument');
    if (!track) return false;
    const nextPatch = normalizeInstrumentPatch(track.instrumentPatch || loadInitialPatch());
    activeTrackIdRef.current = track.id;
    setActiveTrackId(track.id);
    patchRef.current = nextPatch;
    setPatchState(nextPatch);
    undoRef.current = [];
    redoRef.current = [];
    return true;
  }, []);

  const createInstance = useCallback((options = {}) => {
    const initialPatch = normalizeInstrumentPatch(options.patch || loadInitialPatch());
    const result = addInstrumentTrack({ ...options, patch: initialPatch });
    if (!result?.track) return null;
    activeTrackIdRef.current = result.track.id;
    setActiveTrackId(result.track.id);
    patchRef.current = initialPatch;
    setPatchState(initialPatch);
    return result.track;
  }, [addInstrumentTrack]);

  useEffect(() => {
    if (!activeTrackId && instrumentTracks.length) selectTrack(instrumentTracks[0].id);
    if (activeTrackId && !instrumentTracks.some((track) => track.id === activeTrackId)) {
      const fallback = instrumentTracks[0];
      if (fallback) selectTrack(fallback.id);
      else {
        activeTrackIdRef.current = null;
        setActiveTrackId(null);
      }
    }
  }, [activeTrackId, instrumentTracks, selectTrack]);

  // Hosted instruments stream per note: fetch what the arrangement plays, right away on
  // Play and shortly after edits, so playback uses the exact recordings.
  useEffect(() => {
    if (!audioContext) return undefined;
    const handle = window.setTimeout(() => {
      instrumentNotesByTrack(timelineClipsRef.current).forEach((notes, trackId) => {
        enginesRef.current.get(trackId)?.prepareNotes(notes);
      });
    }, isPlaying ? 0 : 1200);
    return () => window.clearTimeout(handle);
  }, [audioContext, isPlaying, timelineClips, instrumentTracks]);

  useEffect(() => {
    if (!audioContext) return undefined;
    const liveIds = new Set(instrumentTracks.map((track) => track.id));
    instrumentTracks.forEach((track) => {
      const buildEngine = (destination) => {
        const next = new NativeInstrumentEngine(audioContext, destination, track.instrumentPatch || createDefaultInstrumentPatch());
        next.sourcePatch = track.instrumentPatch || null;
        sampleBuffersRef.current.get(track.id)?.forEach((buffer, bufferId) => {
          next.registerSampleBuffer(bufferId, buffer);
        });
        next.setTempo(bpm);
        enginesRef.current.set(track.id, next);
        return next;
      };
      try {
        const destination = getTrackInput(track.id);
        if (!destination) return;
        let engine = enginesRef.current.get(track.id);
        if (engine && (engine.ctx !== audioContext || destination.context !== audioContext)) {
          engine.destroy();
          enginesRef.current.delete(track.id);
          engine = null;
        }
        if (!engine) engine = buildEngine(destination);
        else {
          engine.connect(destination);
          // A track-order change updates channel metadata but leaves its patch
          // intact. Avoid rebuilding synth parameters on that UI-only commit.
          if (engine.sourcePatch !== (track.instrumentPatch || null)) {
            engine.updatePatch(track.instrumentPatch || createDefaultInstrumentPatch());
            engine.sourcePatch = track.instrumentPatch || null;
          }
          engine.setTempo(bpm);
        }
        setEngineError(null);
      } catch (error) {
        enginesRef.current.get(track.id)?.destroy();
        enginesRef.current.delete(track.id);
        try {
          const rebuiltDestination = getTrackInput(track.id, { forceRecreate: true });
          if (!rebuiltDestination) throw error;
          buildEngine(rebuiltDestination);
          setEngineError(null);
        } catch (retryError) {
          console.error('Instrument audio graph recovery failed:', retryError);
          setEngineError(retryError?.message || 'Instrument audio graph failed');
        }
      }
    });
    enginesRef.current.forEach((engine, trackId) => {
      if (!liveIds.has(trackId)) {
        engine.destroy();
        enginesRef.current.delete(trackId);
        sampleBuffersRef.current.delete(trackId);
        registerInstrumentSampleBuffers?.(trackId, null);
      }
    });
    setEngineReady(Boolean(activeTrackId && enginesRef.current.has(activeTrackId)));
    return undefined;
  }, [audioContext, activeTrackId, bpm, getTrackInput, instrumentTracks, registerInstrumentSampleBuffers]);

  // Sampled instruments persist a durable stock-asset reference, not a signed
  // URL. Resolve and decode those zones whenever a session/track appears so an
  // AI-created one-shot instrument plays without requiring its window to open.
  useEffect(() => {
    if (!audioContext) return undefined;
    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;

    instrumentTracks.forEach((track) => {
      const zones = normalizeInstrumentPatch(track.instrumentPatch || {})
        .layers.flatMap((layer) => (['sample','granular'].includes(layer.engine) ? layer.sampleZones : []));
      zones.forEach((zone) => {
        const bufferId = String(zone.bufferId || zone.assetId || '');
        const assetId = String(zone.assetId || (zone.sourceType === 'stock' ? zone.bufferId : '') || '');
        if (!bufferId || (!assetId && !zone.storageKey && !zone.url)) return;
        if (sampleBuffersRef.current.get(track.id)?.has(bufferId)) return;
        const loadKey = `${track.id}:${bufferId}`;
        if (sampleAssetLoadsRef.current.has(loadKey)) return;

        const load = (async () => {
          const url = await resolveInstrumentSampleUrl(zone, token);
          if (!url) return;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Instrument sample request failed (${response.status})`);
          const buffer = await audioContext.decodeAudioData((await response.arrayBuffer()).slice(0));
          if (!tracksRef.current.some((candidate) => candidate.id === track.id)) return;
          let trackBuffers = sampleBuffersRef.current.get(track.id);
          if (!trackBuffers) {
            trackBuffers = new Map();
            sampleBuffersRef.current.set(track.id, trackBuffers);
          }
          trackBuffers.set(bufferId, buffer);
          enginesRef.current.get(track.id)?.registerSampleBuffer(bufferId, buffer);
          registerInstrumentSampleBuffers?.(track.id, trackBuffers);
        })().catch((error) => {
          // The engine remains playable through its ordinary fallback graph;
          // a future track/session refresh can retry the stock resolution.
          console.error('Could not load sampled instrument asset:', error);
        }).finally(() => sampleAssetLoadsRef.current.delete(loadKey));
        sampleAssetLoadsRef.current.set(loadKey, load);
      });
    });

    return undefined;
  }, [audioContext, instrumentTracks, registerInstrumentSampleBuffers]);

  useEffect(() => () => {
    enginesRef.current.forEach((engine) => engine.destroy());
    enginesRef.current.clear();
  }, []);

  useEffect(() => {
    if (activeTrackId) enginesRef.current.get(activeTrackId)?.updatePatch(patch);
    try { localStorage.setItem(LAST_PATCH_KEY, JSON.stringify(patch)); } catch (_) { /* storage optional */ }
  }, [activeTrackId, patch]);

  useEffect(() => {
    enginesRef.current.forEach((engine) => engine.setTempo(bpm));
  }, [bpm]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(userPresets)); } catch (_) { /* storage optional */ }
  }, [userPresets]);

  const commitPatch = useCallback((candidate, { history = true } = {}) => {
    const previous = patchRef.current;
    const next = normalizeInstrumentPatch(typeof candidate === 'function' ? candidate(previous) : candidate);
    if (history) {
      undoRef.current.push(cloneInstrumentPatch(previous));
      if (undoRef.current.length > MAX_HISTORY) undoRef.current.shift();
      redoRef.current = [];
    }
    patchRef.current = next;
    setPatchState(next);
    const trackId = activeTrackIdRef.current;
    if (trackId) {
      enginesRef.current.get(trackId)?.updatePatch(next);
      updateTrack(trackId, { instrumentPatch: next });
    }
  }, [updateTrack]);

  const setParam = useCallback((path, value, options) => {
    commitPatch((previous) => updateAtPath(previous, path, value), options);
  }, [commitPatch]);

  /**
   * Put a wavetable (consecutive 2048-sample cycles) on a Core oscillator. It
   * plays at once from memory and is uploaded as a Serum-compatible WAV so
   * the session reopens with it.
   */
  const applyCoreWavetable = useCallback((layerIndex, oscKey, cycles, name) => {
    const frames = Math.floor((cycles?.length || 0) / 2048);
    if (!(cycles instanceof Float32Array) || frames < 1) throw new Error('A wavetable needs at least one 2048-sample cycle.');
    const key = wavetableKey(cycles);
    registerCoreTable(key, cycles);
    const safeIndex = Math.max(0, Math.min(3, Math.round(Number(layerIndex) || 0)));
    const osc = oscKey === 'oscB' ? 'oscB' : 'oscA';
    const path = `layers.${safeIndex}.core.${osc}`;
    const safeName = String(name || 'Wavetable').slice(0, 60);
    const reference = { key, name: safeName, frames, storageKey: '' };
    commitPatch((previous) => updateAtPath(
      updateAtPath(previous, `${path}.customTable`, reference),
      `${path}.table`,
      CORE_CUSTOM_TABLE
    ));

    // Persist in the background; the table already plays locally.
    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;
    const sessionMatch = typeof window !== 'undefined' ? /\/session\/([^/?#]+)/.exec(window.location.pathname) : null;
    if (token && typeof fetch === 'function' && typeof FormData === 'function') {
      const form = new FormData();
      form.append('audio', new Blob([encodeWavetableWav(cycles)], { type: 'audio/wav' }), `${safeName.replace(/[^\w .-]+/g, '_')}.wav`);
      form.append('sessionId', sessionMatch ? decodeURIComponent(sessionMatch[1]) : 'instrument-wavetables');
      form.append('drumName', 'wavetable');
      fetch(`${API_URL}/api/upload/drum-sample`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form })
        .then((response) => (response.ok ? response.json() : null))
        .then((uploaded) => {
          if (!uploaded?.storageKey) return;
          commitPatch((previous) => {
            const current = previous.layers?.[safeIndex]?.core?.[osc]?.customTable;
            return current?.key === key
              ? updateAtPath(previous, `${path}.customTable.storageKey`, uploaded.storageKey)
              : previous;
          }, { history: false });
        })
        .catch(() => { /* stays local until the next import */ });
    }
    return reference;
  }, [commitPatch]);

  /**
   * Import a wavetable (Serum/Vital WAV) or any audio (sliced by pitch) into
   * a Core oscillator.
   */
  const importCoreWavetable = useCallback(async (layerIndex, oscKey, file) => {
    if (!file?.arrayBuffer) throw new Error('Choose a WAV wavetable or an audio file.');
    const imported = await importWavetableFile(file, {
      decodeAudio: audioContext ? (bytes) => audioContext.decodeAudioData(bytes) : null
    });
    applyCoreWavetable(layerIndex, oscKey, imported.cycles, imported.name);
    return imported;
  }, [applyCoreWavetable, audioContext]);

  const loadSampleFiles = useCallback(async (layerIndex, fileList) => {
    const trackId = activeTrackIdRef.current;
    const files = Array.from(fileList || []).filter((file) => file?.arrayBuffer && !file.name?.startsWith('._'));
    if (!audioContext || !trackId || files.length === 0) {
      throw new Error('Select an instrument track and at least one audio file.');
    }
    if (files.length > 128) throw new Error('A sample layer supports up to 128 zones.');
    let trackBuffers = sampleBuffersRef.current.get(trackId);
    if (!trackBuffers) {
      trackBuffers = new Map();
      sampleBuffersRef.current.set(trackId, trackBuffers);
    }
    const decoded = [];
    for (const [index, file] of files.entries()) {
      const bytes = await file.arrayBuffer();
      const buffer = await audioContext.decodeAudioData(bytes.slice(0));
      const bufferId = `local:${trackId}:${file.name}:${file.size}:${file.lastModified || 0}:${index}`;
      trackBuffers.set(bufferId, buffer);
      enginesRef.current.get(trackId)?.registerSampleBuffer(bufferId, buffer);
      // Publish upward so the offline mixdown can load the same buffers; this
      // provider renders inside AudioProvider, so the export cannot reach them.
      registerInstrumentSampleBuffers?.(trackId, trackBuffers);
      const filenameRoot = rootNoteFromFilename(file.name);
      const rootNote = filenameHasExplicitOctave(file.name)
        ? filenameRoot
        : rootNoteFromAudioBuffer(buffer, filenameRoot);
      decoded.push({ id: bufferId, bufferId, name: file.name, rootNote });
      // Give rendering/input a turn between analysis batches when users load a
      // full multisample set; pitch detection is deliberately local and can be
      // CPU-heavy for long files.
      if ((index + 1) % 4 === 0) {
        await new Promise((resolve) => {
          if (typeof requestAnimationFrame === 'function') requestAnimationFrame(resolve);
          else setTimeout(resolve, 0);
        });
      }
    }
    const zones = buildSampleZones(decoded);
    const safeIndex = Math.max(0, Math.min(3, Math.round(Number(layerIndex) || 0)));
    commitPatch((previous) => ({
      ...previous,
      layers: previous.layers.map((layer, index) => index === safeIndex ? {
        ...layer,
        engine: layer.engine === 'granular' ? 'granular' : 'sample',
        enabled: true,
        name: files.length === 1 ? files[0].name.replace(/\.[^.]+$/, '').slice(0, 80) : `${files.length}-zone instrument`,
        sampleZones: zones,
        sampleMode: 'gate',
        octave: 0,
        semitone: 0,
        detune: 0,
        level: Math.min(0.85, Math.max(0.5, layer.level || 0.72))
      } : layer)
    }));
    return { files: decoded.length, zones };
  }, [audioContext, commitPatch, registerInstrumentSampleBuffers]);

  const registerImportedSamples = useCallback((trackId, buffers) => {
    sampleBuffersRef.current.set(trackId, new Map(buffers));
    buffers.forEach((buffer, id) => enginesRef.current.get(trackId)?.registerSampleBuffer(id, buffer));
    registerInstrumentSampleBuffers?.(trackId, buffers);
  }, [registerInstrumentSampleBuffers]);

  const getActiveSampleBuffers = useCallback(() => {
    const trackId = activeTrackIdRef.current;
    return new Map(trackId ? (sampleBuffersRef.current.get(trackId) || []) : []);
  }, []);

  const applyAiCommands = useCallback((commands, options = {}) => {
    const result = applyAiPatchCommands(patchRef.current, commands);
    const audit = auditInstrumentPatch(result.patch);
    const { requirePlayable = false, ...commitOptions } = options;
    const committed = result.applied.length > 0 && (!requirePlayable || audit.playable);
    if (committed) commitPatch(result.patch, commitOptions);
    return { ...result, audit, committed };
  }, [commitPatch]);

  const getAiSoundSpec = useCallback(() => createAiSoundSpec(patchRef.current), []);
  const auditPatch = useCallback((candidate = patchRef.current) => auditInstrumentPatch(candidate), []);

  const undo = useCallback(() => {
    const previous = undoRef.current.pop();
    if (!previous) return false;
    redoRef.current.push(cloneInstrumentPatch(patchRef.current));
    patchRef.current = previous;
    setPatchState(previous);
    const trackId = activeTrackIdRef.current;
    if (trackId) {
      enginesRef.current.get(trackId)?.updatePatch(previous);
      updateTrack(trackId, { instrumentPatch: previous });
    }
    return true;
  }, [updateTrack]);

  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next) return false;
    undoRef.current.push(cloneInstrumentPatch(patchRef.current));
    patchRef.current = next;
    setPatchState(next);
    const trackId = activeTrackIdRef.current;
    if (trackId) {
      enginesRef.current.get(trackId)?.updatePatch(next);
      updateTrack(trackId, { instrumentPatch: next });
    }
    return true;
  }, [updateTrack]);

  const activeEngine = useCallback(() => enginesRef.current.get(activeTrackIdRef.current), []);

  /** DrumContext registers its pitched one-shot trigger here. */
  const registerDrumTrigger = useCallback((trigger) => {
    drumTriggerRef.current = typeof trigger === 'function' ? trigger : null;
  }, []);

  /**
   * Point live input at whatever the timeline has selected. An instrument
   * target also focuses that track, so the panel and the keys never disagree
   * about which patch is playing.
   */
  const setMidiTarget = useCallback((target) => {
    midiTargetRef.current = target || null;
    if (target?.kind === MIDI_TARGET_INSTRUMENT && target.trackId
        && target.trackId !== activeTrackIdRef.current) {
      activeTrackIdRef.current = target.trackId;
      setActiveTrackId(target.trackId);
    }
  }, []);

  /**
   * Beat to record an input at. A hardware event timestamp resolves against the
   * audio clock; without one we fall back to the animation-frame playhead.
   */
  const captureBeat = useCallback((eventTime) => {
    if (Number.isFinite(Number(eventTime)) && beatAtPerformanceTime) {
      const precise = beatAtPerformanceTime(Number(eventTime));
      if (Number.isFinite(precise)) return Math.max(0, precise);
    }
    return Math.max(0, Number(transportClock.getBeat()) || 0);
  }, [beatAtPerformanceTime]);

  const stopArp = useCallback((trackId = null) => {
    arpPlayheadRef.current.length = 0;
    clearSchedulerInterval(arpTimerRef.current);
    arpTimerRef.current = null;
    arpSchedulerRef.current.reset();
    releaseArpeggiatorVoices(activeArpVoicesRef.current, trackId);
  }, []);

  /**
   * Fill the next ~120 ms of arp steps on the audio clock. Runs on the worker
   * clock every 25 ms; settings are read live, so tweaks never restart the
   * pattern. While the transport runs, steps sit on the song grid.
   */
  const tickArp = useCallback(() => {
    const engine = activeEngine();
    const held = [...heldArpNotesRef.current.values()];
    if (!engine || !audioContext || held.length === 0) {
      if (held.length === 0) stopArp();
      return;
    }
    const arp = patchRef.current.arp;
    const now = audioContext.currentTime;
    const transport = isPlayingRef.current ? getTransportSchedule?.(0.001) : null;
    const bps = transport?.bps || Math.max(0.5, bpmRef.current / 60);
    const events = arpSchedulerRef.current.schedule({
      now, horizon: now + ARP_LOOKAHEAD_SECONDS, held, arp, bps,
      beatAtNow: transport ? transport.unwrappedBeat : null
    });
    const trackId = activeTrackIdRef.current;
    const active = activeArpVoicesRef.current;
    active.forEach((voice, id) => { if (voice.end < now) active.delete(id); });
    const recording = isRecordingRef.current && isPlayingRef.current;
    const playhead = arpPlayheadRef.current;
    events.forEach((event) => {
      if (!playhead.length || playhead[playhead.length - 1].step !== event.step) {
        playhead.push({ time: event.time, step: event.step });
        if (playhead.length > 16) playhead.shift();
      }
      engine.noteOn(event.note, event.velocity, event.id, event.time);
      engine.noteOff(event.id, false, event.end);
      active.set(event.id, { trackId, engine, end: event.end });
      // The arp's notes are the performance: record them, not the held keys.
      if (recording) {
        const startBeat = Math.max(0, (Number(transportClock.getBeat()) || 0) + (event.time - now) * bps);
        writeRecordedNoteRef.current?.({ trackId, note: event.note, velocity: event.velocity, startBeat }, startBeat + event.durationBeats);
      }
    });
  }, [activeEngine, audioContext, getTransportSchedule, stopArp]);

  /** The arp step sounding now (global step index), or null. Cheap: for animation frames. */
  const getArpStep = useCallback(() => {
    const now = audioContext?.currentTime;
    const playhead = arpPlayheadRef.current;
    if (!Number.isFinite(now) || !playhead.length) return null;
    for (let index = playhead.length - 1; index >= 0; index -= 1) {
      if (playhead[index].time <= now) return playhead[index].step;
    }
    return null;
  }, [audioContext]);

  const ensureArp = useCallback(() => {
    if (arpTimerRef.current || heldArpNotesRef.current.size === 0) return;
    arpTimerRef.current = setSchedulerInterval(() => tickArp(), 25);
    tickArp();
  }, [tickArp]);

  // Turning the arp off stops it; turning latch off drops notes no longer held.
  useEffect(() => {
    if (!patch.arp.enabled) {
      heldArpNotesRef.current.clear();
      pressedArpKeysRef.current.clear();
      stopArp();
    } else if (!patch.arp.latch) {
      heldArpNotesRef.current.forEach((_, id) => {
        if (!pressedArpKeysRef.current.has(id)) heldArpNotesRef.current.delete(id);
      });
      if (heldArpNotesRef.current.size === 0) stopArp();
    }
  }, [patch.arp.enabled, patch.arp.latch, stopArp]);

  // A new instrument or a closed provider must not leave the arp running.
  useEffect(() => () => clearSchedulerInterval(arpTimerRef.current), []);

  const ensureRecordingClip = useCallback((trackId, absoluteBeat) => {
    const take = recordingTakeRef.current;
    if (take?.active) {
      const storedTakeClip = take.clips.get(trackId);
      const takeClip = storedTakeClip || null;
      if (takeClip) return takeClip;
    }
    const existing = recordingTargetClip(timelineClipsRef.current, trackId,
      take?.active ? take.startBeat : absoluteBeat, { take: Boolean(take?.active) });
    if (existing) {
      if (take?.active) take.clips.set(trackId, existing);
      return existing;
    }
    const track = tracksRef.current.find((entry) => entry.id === trackId);
    const startBeat = take?.active
      ? take.startBeat
      : Math.max(0, Math.floor(absoluteBeat / 4) * 4);
    const clipData = {
      kind: 'instrument', instrumentTrackId: trackId,
      id: `instrument-pattern-${Date.now().toString(36)}`,
      name: `${track?.name || 'Instrument'} MIDI`, pattern: {}, noteData: {}, noteEvents: [],
      ppq: 96, patternLength: 16, rowViewModes: { 0: 'piano' },
      drumSounds: [{ key: `instrument:${trackId}`, name: track?.name || 'Instrument', playbackMode: 'instrument' }],
      bpm
    };
    const clip = addClipToTimeline(trackId, startBeat, 4, 'pattern', clipData, {
      id: take?.active ? `${take.id}:${trackId}` : undefined
    });
    // No room on the lane returns no clip; the take must never hold an empty entry.
    if (take?.active && clip) take.clips.set(trackId, clip);
    return clip;
  }, [addClipToTimeline, bpm]);

  /** Commit every buffered note, one clip update per clip. */
  const flushRecordedNotes = useCallback(() => {
    if (recordingFlushTimerRef.current) {
      window.clearTimeout(recordingFlushTimerRef.current);
      recordingFlushTimerRef.current = null;
    }
    const pending = pendingRecordedNotesRef.current;
    if (pending.size === 0) return;
    pendingRecordedNotesRef.current = new Map();

    pending.forEach(({ trackId, events }, clipId) => {
      const take = recordingTakeRef.current;
      const takeClip = take?.active ? take.clips.get(trackId) : null;
      const freshest = takeClip || timelineClipsRef.current.find((entry) => entry.id === clipId);
      if (!freshest?.clipData) return;
      const nextData = appendRecordedNotes(freshest.clipData, events);
      if (!nextData || nextData === freshest.clipData) return;
      const updated = updatePatternClip(freshest.id, nextData, { fromRecording: true });
      if (take?.active && updated) take.clips.set(trackId, updated);
    });
  }, [updatePatternClip]);

  const writeRecordedNote = useCallback((capture, endBeat) => {
    if (!capture) return null;
    const clip = ensureRecordingClip(capture.trackId, capture.startBeat);
    if (!clip?.clipData) return null;
    const takeClip = recordingTakeRef.current?.active
      ? recordingTakeRef.current.clips.get(capture.trackId)
      : null;
    const freshest = takeClip || timelineClipsRef.current.find((entry) => entry.id === clip.id) || clip;
    const ppq = Number(freshest.clipData.ppq) || 96;
    const { startTick, durationTicks } = recordedNoteTiming(
      freshest.startBeat, capture.startBeat, endBeat, ppq
    );
    const event = {
      id: `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      drumIndex: 0, pitches: [capture.note], startTick, durationTicks,
      velocity: Math.max(0.01, Math.min(1, capture.velocity || 0.82))
    };

    const pending = pendingRecordedNotesRef.current;
    const bucket = pending.get(freshest.id) || { trackId: capture.trackId, events: [] };
    bucket.events.push(event);
    pending.set(freshest.id, bucket);
    if (!recordingFlushTimerRef.current) {
      recordingFlushTimerRef.current = window.setTimeout(() => {
        recordingFlushTimerRef.current = null;
        flushRecordedNotes();
      }, RECORDING_FLUSH_MS);
    }
    return freshest;
  }, [ensureRecordingClip, flushRecordedNotes]);

  // Keep the arp's late-bound references current.
  useEffect(() => { writeRecordedNoteRef.current = writeRecordedNote; }, [writeRecordedNote]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const noteOnForTrack = useCallback(async (trackId, note, velocity = 0.82, sourceId = null, options = {}) => {
    if (audioContext?.state === 'suspended') await audioContext.resume().catch(() => {});
    const targetTrackId = trackId || activeTrackIdRef.current;
    const engine = enginesRef.current.get(targetTrackId);
    if (!targetTrackId || !engine) return null;
    const id = sourceId || `note:${note}`;
    const sourceKey = `${targetTrackId}:${id}`;
    const prior = heldVoicesRef.current.get(sourceKey);
    if (prior) engine.noteOff(prior.voiceId, true);
    heldVoicesRef.current.set(sourceKey, { trackId: targetTrackId, note, voiceId: id });
    setActiveNotes((previous) => previous.includes(note) ? previous : [...previous, note]);
    const arpOn = patchRef.current.arp.enabled;
    if (isRecording && isPlaying && options.record !== false && !arpOn) {
      recordingNotesRef.current.set(sourceKey, {
        trackId: targetTrackId, note, velocity, startBeat: captureBeat(options.eventTime)
      });
    }
    if (arpOn) {
      // Latched: the first key after letting go of everything starts a new chord.
      if (patchRef.current.arp.latch && pressedArpKeysRef.current.size === 0) heldArpNotesRef.current.clear();
      pressedArpKeysRef.current.add(id);
      arpPressOrderRef.current += 1;
      heldArpNotesRef.current.set(id, { note, velocity, order: arpPressOrderRef.current });
      ensureArp();
      return id;
    }
    return engine.noteOn(note, velocity, id) || id;
  }, [audioContext, captureBeat, ensureArp, isPlaying, isRecording]);

  const noteOffForTrack = useCallback((trackId, note, sourceId = null, options = {}) => {
    const targetTrackId = trackId || activeTrackIdRef.current;
    const id = sourceId || `note:${note}`;
    const sourceKey = `${targetTrackId}:${id}`;
    const held = heldVoicesRef.current.get(sourceKey);
    const releasedNote = Number.isFinite(Number(note)) ? Number(note) : held?.note;
    setActiveNotes((previous) => previous.filter((entry) => entry !== releasedNote));
    pressedArpKeysRef.current.delete(id);
    if (!patchRef.current.arp.latch) heldArpNotesRef.current.delete(id);
    const recorded = recordingNotesRef.current.get(sourceKey);
    if (recorded) writeRecordedNote(recorded, captureBeat(options.eventTime));
    recordingNotesRef.current.delete(sourceKey);
    heldVoicesRef.current.delete(sourceKey);
    if (patchRef.current.arp.enabled) {
      if (heldArpNotesRef.current.size === 0) stopArp();
      return;
    }
    enginesRef.current.get(targetTrackId)?.noteOff(id);
  }, [captureBeat, stopArp, writeRecordedNote]);

  const noteOn = useCallback((note, velocity = 0.82, sourceId = null, options = {}) => {
    const target = midiTargetRef.current;
    // A selected drum lane takes the keyboard: every key fires that lane
    // pitched to the note, so an 808 lane plays a bassline rather than one
    // fixed hit. Instrument targets already redirected activeTrackId, and an
    // audio track leaves input on the current instrument rather than muting it.
    if (target?.kind === MIDI_TARGET_DRUM && target.drumKey && drumTriggerRef.current) {
      drumTriggerRef.current(target.drumKey, note);
      return null;
    }
    return noteOnForTrack(activeTrackIdRef.current, note, velocity, sourceId, options);
  }, [noteOnForTrack]);

  const noteOff = useCallback((note, sourceId = null, options = {}) => {
    // Drum one-shots ring out on their own envelope; there is nothing to release.
    if (midiTargetRef.current?.kind === MIDI_TARGET_DRUM) return undefined;
    return noteOffForTrack(activeTrackIdRef.current, note, sourceId, options);
  }, [noteOffForTrack]);

  const releaseLiveInput = useCallback((trackId = null) => {
    const endBeat = transportClock.getBeat();
    recordingNotesRef.current.forEach((capture, key) => {
      if (!trackId || capture.trackId === trackId) {
        writeRecordedNote(capture, endBeat);
        recordingNotesRef.current.delete(key);
      }
    });
    flushRecordedNotes();
    heldArpNotesRef.current.clear();
    pressedArpKeysRef.current.clear();
    stopArp(trackId);
    setActiveNotes(releaseHeldInputVoices(heldVoicesRef.current, enginesRef.current, trackId));
  }, [flushRecordedNotes, stopArp, writeRecordedNote]);

  const panic = useCallback((trackId = null) => {
    releaseLiveInput(trackId);
    if (trackId) enginesRef.current.get(trackId)?.allNotesOff(true);
    else enginesRef.current.forEach((engine) => engine.allNotesOff(true));
  }, [releaseLiveInput]);

  // One transport roll is one take. Note gaps are silence inside that take,
  // never boundaries that create additional clips.
  useEffect(() => {
    const captureActive = Boolean(isRecording && isPlaying);
    if (captureActive && !recordingTakeRef.current?.active) {
      const startBeat = Math.max(0, Number(transportClock.getBeat()) || 0);
      recordingTakeRef.current = {
        id: `instrument-take-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        active: true,
        startBeat,
        endBeat: startBeat,
        clips: new Map()
      };
    }

    if (captureActive) {
      if (recordingTakeTimerRef.current) window.clearInterval(recordingTakeTimerRef.current);
      recordingTakeTimerRef.current = window.setInterval(() => {
        const take = recordingTakeRef.current;
        if (!take?.active) return;
        take.endBeat = Math.max(take.endBeat, Number(transportClock.getBeat()) || take.endBeat);
      }, 25);
      return () => {
        if (recordingTakeTimerRef.current) window.clearInterval(recordingTakeTimerRef.current);
        recordingTakeTimerRef.current = null;
      };
    }

    const take = recordingTakeRef.current;
    if (!take?.active) return undefined;
    const currentBeat = Number(transportClock.getBeat());
    const endBeat = Math.max(take.endBeat, Number.isFinite(currentBeat) ? currentBeat : take.endBeat);

    // Close notes still held at Pause/Stop before extending through any trailing
    // silence. This prevents stuck captures and preserves their real duration.
    recordingNotesRef.current.forEach((capture, key) => {
      writeRecordedNote(capture, endBeat);
      recordingNotesRef.current.delete(key);
    });
    // Commit the buffer before measuring the take, so the trailing-silence
    // extension below is computed against every captured note.
    flushRecordedNotes();

    take.clips.forEach((storedClip) => {
      if (!storedClip) return;
      // The take's copy includes the notes just flushed above; the clip ref lags a render
      // behind, and writing it back would drop a note still held at Stop.
      const clip = storedClip.clipData ? storedClip : timelineClipsRef.current.find((entry) => entry?.id === storedClip.id);
      if (!clip?.clipData) return;
      updatePatternClip(clip.id, {
        ...clip.clipData,
        patternLength: patternLengthForRecordingTake(
          clip.startBeat,
          endBeat,
          clip.clipData.patternLength
        )
      });
    });
    take.active = false;
    recordingTakeRef.current = null;
    return undefined;
  }, [flushRecordedNotes, isPlaying, isRecording, updatePatternClip, writeRecordedNote]);

  // Never strand buffered notes if the provider goes away mid-take.
  useEffect(() => () => {
    if (recordingFlushTimerRef.current) window.clearTimeout(recordingFlushTimerRef.current);
    recordingFlushTimerRef.current = null;
  }, []);

  useEffect(() => {
    const release = () => releaseLiveInput();
    const visibility = () => { if (document.visibilityState === 'hidden') release(); };
    window.addEventListener('blur', release);
    window.addEventListener('pointercancel', release);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      window.removeEventListener('blur', release);
      window.removeEventListener('pointercancel', release);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [releaseLiveInput]);

  useEffect(() => {
    if (!isPlaying || !audioContext) {
      scheduledEventsRef.current.clear();
      schedulerLastBeatRef.current = null;
      schedulerTickRef.current = null;
      enginesRef.current.forEach((engine) => engine.allNotesOff(true));
      return undefined;
    }
    const scheduleWindow = () => {
      const firstPass = schedulerLastBeatRef.current == null;
      const schedule = getTransportSchedule?.(LOOKAHEAD_SECONDS, { includeStart: firstPass });
      if (!schedule) return;
      const { now, bps, unwrappedBeat, segments, catchupBeats = 0 } = schedule;
      const secondsPerBeat = 1 / bps;

      // A seek in either direction invalidates everything already queued. The
      // transport only ever advances with the audio clock, so any other change
      // in position is a jump; with a long window, notes queued for the old
      // position would otherwise keep sounding after a seek forwards.
      const last = schedulerLastBeatRef.current;
      const expected = last == null ? null : last + Math.max(0, now - schedulerLastTimeRef.current) * bps;
      if (expected != null && Math.abs(unwrappedBeat - expected) > 0.1) {
        scheduledEventsRef.current.clear();
        enginesRef.current.forEach((engine) => engine.allNotesOff(true));
      }
      schedulerLastBeatRef.current = unwrappedBeat;
      schedulerLastTimeRef.current = now;

      // Keys carry the UNWRAPPED beat, so the same note is a new event on each
      // pass through the loop while still never being queued twice.
      const seen = scheduledEventsRef.current;
      if (seen.size > MAX_SCHEDULED_KEYS) seen.clear();

      timelineClipsRef.current.forEach((clip) => {
        if (clip.clipType !== 'pattern' || clip.clipData?.kind !== 'instrument') return;
        const engine = enginesRef.current.get(clip.clipData.instrumentTrackId || clip.trackId);
        if (!engine) return;
        const ppq = Number(clip.clipData.ppq) || 96;
        const events = clip.clipData.noteEvents || [];

        segments.forEach((segment) => {
          events.forEach((event, index) => {
            const eventBeat = clip.startBeat + ((Number(event.startTick) || 0) / ppq);
            if (eventBeat < segment.fromBeat - 1e-6 || eventBeat >= segment.toBeat - 1e-6) return;

            const beatsFromNow = segment.beatOffset + (eventBeat - segment.fromBeat) - catchupBeats;
            const durationBeats = Math.max(1 / ppq, (Number(event.durationTicks) || (ppq / 4)) / ppq);
            // Do not emit an expired note if the first scheduler pass followed a
            // severe stall. Normally catch-up is only a few milliseconds.
            if (beatsFromNow + durationBeats <= 0) return;
            const absoluteBeat = unwrappedBeat + beatsFromNow;
            const key = `${clip.id}:${event.id || index}:${absoluteBeat.toFixed(4)}`;
            if (seen.has(key)) return;
            seen.add(key);

            const when = now + Math.max(MIN_SCHEDULE_LEAD_SECONDS, beatsFromNow * secondsPerBeat);
            const intendedEnd = now + ((beatsFromNow + durationBeats) * secondsPerBeat);
            const noteOffTime = Math.max(when + MIN_SCHEDULE_LEAD_SECONDS, intendedEnd);
            const velocity = Number(event.velocity) || 0.82;
            // A chord is ONE event carrying several pitches; each needs its own
            // voice id or the second note retriggers and cancels the first.
            eventPitches(event).forEach((note) => {
              const voiceId = `timeline:${key}:${note}`;
              engine.noteOn(note, velocity, voiceId, when);
              engine.noteOff(voiceId, false, noteOffTime);
            });
          });
        });
      });
    };
    schedulerTickRef.current = scheduleWindow;
    scheduleWindow();
    const timer = setSchedulerInterval(scheduleWindow, 25);
    return () => {
      clearSchedulerInterval(timer);
      if (schedulerTickRef.current === scheduleWindow) schedulerTickRef.current = null;
    };
  }, [audioContext, bpm, isPlaying, getTransportSchedule]);

  // Cancel notes queued after a tiny guard window and refill the lookahead.
  // Voices already sounding (and the next render quantum) are preserved.
  const invalidateQueuedNotes = (context) => {
    const cutoff = context.currentTime + LIVE_EDIT_GUARD_SECONDS;
    enginesRef.current.forEach((engine) => engine.cancelScheduledVoices(cutoff));

    // Keep dedupe keys for imminent voices we deliberately preserved; otherwise
    // the synchronous refill would retrigger and cancel those same voices.
    const liveVoiceIds = [];
    enginesRef.current.forEach((engine) => {
      engine.voices.forEach((_, voiceId) => liveVoiceIds.push(String(voiceId)));
    });
    scheduledEventsRef.current = new Set(
      [...scheduledEventsRef.current].filter((key) => (
        liveVoiceIds.some((voiceId) => voiceId.startsWith(`timeline:${key}:`))
      ))
    );
    schedulerTickRef.current?.();
  };

  // Piano-roll edits invalidate notes queued in Web Audio's lookahead window.
  // Preserve voices already sounding (and the next render quantum), cancel only
  // later queued notes, then synchronously refill the edited window. A global
  // allNotesOff here used to blank every instrument and click on every edit.
  // Deliberately skips the transport-start run: the note scheduler effect above
  // runs earlier in the same commit and has just queued the notes at the start
  // beat. allNotesOff would kill those voices before they sound (noteOn registers
  // a voice synchronously even when its scheduledTime is still in the future),
  // and the scheduler never retries a beat the transport has passed — which
  // silenced the first chord of a progression. Mirrors the audio-clip
  // scheduler's lastAuditionClipsRef guard in AudioContext.
  useEffect(() => {
    const { clips, cancel } = nextAuditionState(
      lastAuditionClipsRef.current, timelineClips, isPlaying,
      { recordingWriteClips: recordingWriteClipsRef?.current }
    );
    lastAuditionClipsRef.current = clips;
    if (!cancel || !audioContext) return;
    invalidateQueuedNotes(audioContext);
  // invalidateQueuedNotes only reads refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, isPlaying, timelineClips, recordingWriteClipsRef]);

  // A tempo change moves every queued note's time. The transport rebases at
  // its own cutoff; re-time the instrument lookahead the same way instead of
  // letting ~120 ms of notes play at the old tempo.
  const lastScheduledBpmRef = useRef(bpm);
  useEffect(() => {
    const previous = lastScheduledBpmRef.current;
    lastScheduledBpmRef.current = bpm;
    if (!isPlaying || !audioContext || previous === bpm) return;
    // This provider's effects run before AudioProvider's, which is where the
    // transport rebases to the new tempo. Refill once that commit settles.
    queueMicrotask(() => invalidateQueuedNotes(audioContext));
  // invalidateQueuedNotes only reads refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, isPlaying, bpm]);

  const setMpeSettings = useCallback((next) => {
    setMpeSettingsState((previous) => {
      const merged = { ...previous, ...next };
      mpeInputRef.current.setMode(merged.mode);
      mpeInputRef.current.setBendRange(merged.bendRange);
      setMpeActive(mpeInputRef.current.active);
      try { localStorage.setItem('cookup_mpe', JSON.stringify(merged)); } catch (_) { /* private mode */ }
      return merged;
    });
  }, []);

  const enableMidi = useCallback(() => {
    if (!navigator.requestMIDIAccess) {
      setMidiStatus('unavailable');
      return Promise.resolve(false);
    }
    setMidiStatus('connecting');
    const onMessage = (event) => {
      // The piano roll owns live MIDI monitoring while its keyboard mode is on.
      if (typeof window !== 'undefined' && window.__cookupPianoKeysOn) return;
      // event.timeStamp is the hardware-accurate moment the message arrived, on
      // the performance.now() timeline. Timing the note from when this handler
      // happens to run instead adds all of the main thread's jitter.
      const eventTime = event.timeStamp;
      const router = mpeInputRef.current;
      const wasActive = router.active;
      router.handle(event.data).forEach((action) => {
        const engine = activeEngine();
        if (action.type === 'noteOn') noteOn(action.note, action.velocity, action.id, { eventTime });
        else if (action.type === 'noteOff') noteOff(action.note, action.id, { eventTime });
        else if (action.type === 'expression') engine?.setNoteExpression?.(action.id, action);
        else if (action.type === 'pitchBend') engine?.setPitchBend(action.value);
        else if (action.type === 'aftertouch') engine?.setAftertouch?.(action.value);
        else if (action.type === 'sustain') engine?.setSustain?.(action.down);
        else if (action.type === 'modWheel') engine?.setModWheel?.(action.value);
        else if (action.type === 'allNotesOff') engine?.allNotesOff?.(action.immediate);
      });
      if (router.active !== wasActive) setMpeActive(router.active);
    };
    const attach = () => {
      const access = midiAccessRef.current;
      if (!access) return;
      midiInputCleanupRef.current?.();
      midiInputCleanupRef.current = subscribeMidiInputs(access, onMessage, (count, devices) => {
        setMidiInputs(devices);
        setMidiStatus(count > 0 ? 'connected' : 'ready');
      }, { selectedInputId: selectedMidiInputIdRef.current });
    };
    if (midiAccessRef.current) {
      attach();
      return Promise.resolve(true);
    }
    return navigator.requestMIDIAccess().then((result) => {
      midiAccessRef.current = result;
      attach();
      return true;
    }).catch(() => {
      setMidiStatus('denied');
      return false;
    });
  }, [activeEngine, noteOff, noteOn]);

  const setMidiInputDevice = useCallback((inputId = 'all') => {
    const next = String(inputId || 'all');
    selectedMidiInputIdRef.current = next;
    setSelectedMidiInputIdState(next);
    try { localStorage.setItem('cookup_midi_input', next); } catch (_) {}
    // Rebuild listeners immediately; requestMIDIAccess is cached after the
    // first grant, so this does not display another browser permission prompt.
    return enableMidi();
  }, [enableMidi]);

  // Device input belongs to the session engine, not to a particular open tile.
  // Keep it alive when the instrument or piano-roll tab is not currently shown.
  useEffect(() => { if (!isPreviewPlayer()) void enableMidi(); }, [enableMidi]);

  useEffect(() => {
    const cleanupRef = midiInputCleanupRef;
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      midiAccessRef.current = null;
    };
  }, []);

  const loadPreset = useCallback((preset) => {
    if (activeTrackIdRef.current) panic(activeTrackIdRef.current);
    const selected = preset.stockAsset ? stockSampleInstrumentPatch(preset.stockAsset) : preset;
    if (selected) commitPatch(cloneInstrumentPatch(selected));
  }, [commitPatch, panic]);

  const savePreset = useCallback((name = patchRef.current.name) => {
    const saved = normalizeInstrumentPatch({
      ...cloneInstrumentPatch(patchRef.current),
      id: `user-${Date.now().toString(36)}`,
      name: String(name || 'Untitled Patch'),
      category: patchRef.current.category || 'User'
    });
    setUserPresets((previous) => [...previous, saved]);
    commitPatch(saved, { history: false });
    return saved;
  }, [commitPatch]);

  const deleteUserPreset = useCallback((id) => {
    setUserPresets((previous) => previous.filter((preset) => preset.id !== id));
  }, []);

  const exportPatch = useCallback(() => {
    const blob = new Blob([JSON.stringify(patchRef.current, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${patchRef.current.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'cookup-patch'}.cookup.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, []);

  const waitForPlaybackReady = useCallback(async () => {
    await Promise.all([...sampleAssetLoadsRef.current.values()]);
    await Promise.all([...enginesRef.current.values()].map(engine => engine.ready()));
    for (const engine of enginesRef.current.values()) {
      if (engine.coreError || engine.afmError) throw new Error('This host could not start the instrument audio engine.');
      await Promise.all([...(engine.coreLayers?.values() || [])].flatMap(entry => entry.tableLoads || []));
      if ([...(engine.coreLayers?.values() || [])].some(entry => (entry.tableKeys || []).some((key, slot) => key && !entry.tables[slot]))) {
        throw new Error('An imported wavetable could not be loaded.');
      }
    }
    for (const track of tracksRef.current.filter(track => track.type === 'instrument')) {
      if (!enginesRef.current.has(track.id)) throw new Error('An instrument is still loading.');
      const zones = normalizeInstrumentPatch(track.instrumentPatch || {}).layers
        .flatMap(layer => ['sample','granular'].includes(layer.engine) ? layer.sampleZones : []);
      if (zones.some(zone => !sampleBuffersRef.current.get(track.id)?.has(String(zone.bufferId || zone.assetId || '')))) {
        throw new Error('An instrument sample could not be loaded. Refresh and retry.');
      }
    }
  }, []);
  const value = useMemo(() => ({
    waitForPlaybackReady,
    patch,
    instrumentTracks,
    activeTrack,
    activeTrackId,
    selectTrack,
    createInstance,
    setPatch: commitPatch,
    setParam,
    loadSampleFiles,
    importCoreWavetable,
    applyCoreWavetable,
    getActiveSampleBuffers,
    registerImportedSamples,
    applyAiCommands,
    getAiSoundSpec,
    auditPatch,
    aiCapabilities: INSTRUMENT_AI_CAPABILITIES,
    undo,
    redo,
    noteOn,
    noteOff,
    noteOnForTrack,
    noteOffForTrack,
    setMidiTarget,
    registerDrumTrigger,
    releaseLiveInput,
    panic,
    activeNotes,
    engineReady,
    engineError,
    midiStatus,
    midiInputs,
    selectedMidiInputId,
    setMidiInputDevice,
    enableMidi,
    mpeSettings,
    setMpeSettings,
    mpeActive,
    getArpStep,
    analyser: enginesRef.current.get(activeTrackId)?.getAnalyser() || null,
    factoryPresets,
    stockSamplePool,
    presetLibraryError,
    presetLibraryLoading,
    reloadPresetLibrary,
    userPresets,
    loadPreset,
    savePreset,
    deleteUserPreset,
    exportPatch
  }), [
    factoryPresets, stockSamplePool, presetLibraryError, presetLibraryLoading, reloadPresetLibrary, waitForPlaybackReady, activeNotes, activeTrack, activeTrackId, commitPatch, createInstance, deleteUserPreset, engineError, engineReady, exportPatch,
    enableMidi, mpeSettings, setMpeSettings, mpeActive, getArpStep, loadPreset, midiInputs, midiStatus, selectedMidiInputId, setMidiInputDevice, noteOff, noteOn, panic, patch, redo, releaseLiveInput, savePreset,
    setMidiTarget, registerDrumTrigger,
    selectTrack, setParam, loadSampleFiles, importCoreWavetable, applyCoreWavetable, registerImportedSamples, getActiveSampleBuffers, applyAiCommands, getAiSoundSpec, auditPatch, undo, userPresets, instrumentTracks, noteOffForTrack, noteOnForTrack
  ]);

  return <InstrumentContext.Provider value={value}>{children}</InstrumentContext.Provider>;
};

export default InstrumentContext;
